// useSaveQueue — the impure runner of the editor's save order (saveQueue.ts
// is the pure half). It owns the three things the pure queue cannot: the idle
// timer, the page listeners (hidden or closed: send now), and the port calls,
// whose outcomes it feeds back as events. Nothing here decides an order; it
// only runs the effects `stepSaveQueue` returns.

export type { StructuralRun };
export { useSaveQueue };

import { exhaustSwitchCase, Result } from "@bilda/utils";
import { useEffect, useRef, useState } from "react";
import type { ExternalError } from "../../app/externalError.js";
import type { PortResult } from "../../app/ports/result.js";
import type { AppPorts } from "../../app/ports.js";
import type { ElementId } from "../Editor/types.js";
import {
    emptySaveQueue,
    type Lane,
    laneOf,
    type Paths,
    type SaveEffect,
    type SaveEvent,
    type SaveQueue,
    stepSaveQueue,
} from "./saveQueue.js";

// A structural edit as the runner sends it: the port call(s), given the hash
// to guard with and the paths as they are when its turn comes, and what a
// refusal does (take the optimistic change back, say why).
type StructuralRun = {
    readonly persist: (
        materialId: string,
        hash: string,
        paths: Paths,
    ) => PortResult<{
        readonly contentHash: string;
        readonly path?: readonly string[];
    }>;
    readonly refused: (error: ExternalError) => void;
};

type SaveQueueApi = {
    // The active material's lane (its hash and paths are the editor's).
    readonly lane: Lane<StructuralRun>;
    // A material's lane as of the last event (for a read that lands after
    // later renders).
    readonly laneNow: (materialId: string) => Lane<StructuralRun>;
    readonly dispatch: (event: SaveEvent<StructuralRun>) => void;
};

// The text a block reads now, when a save was refused because the block
// changed elsewhere since the author started (undefined: any other error).
const changedElsewhere = (error: ExternalError): string | undefined =>
    error._tag === "External.Refused" &&
    error.errorType === "element_changed_since_read"
        ? error.current
        : undefined;

const useSaveQueue = ({
    ports,
    activeMaterialId,
    held,
    onSaved,
    onRefused,
    onConflict,
    onShowText,
}: {
    readonly ports: Pick<AppPorts, "updateElementText" | "patchElementAttrs">;
    readonly activeMaterialId: string | null;
    // The block whose "/" menu or Ask line is in progress, read when the
    // idle timer fires.
    readonly held: () => ElementId | undefined;
    readonly onSaved: () => void;
    // A field save was refused (the typed text stays; the notice says so).
    readonly onRefused: () => void;
    // A block's save waits for the author's pick: offer Keep mine / Take
    // theirs.
    readonly onConflict: (materialId: string, id: ElementId) => void;
    // Theirs was taken: show `text` in the block.
    readonly onShowText: (
        materialId: string,
        id: ElementId,
        text: string,
    ) => void;
}): SaveQueueApi => {
    const [queue, setQueue] =
        useState<SaveQueue<StructuralRun>>(emptySaveQueue);
    // The queue as of the last event: port answers land after later renders.
    const latest = useRef(queue);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    // Read at the moment they are needed, not at the render that made them.
    const deps = useRef({
        ports,
        held,
        onSaved,
        onRefused,
        onConflict,
        onShowText,
    });
    deps.current = { ports, held, onSaved, onRefused, onConflict, onShowText };

    const dispatch = (event: SaveEvent<StructuralRun>) => {
        const step = stepSaveQueue(latest.current, event);
        latest.current = step.state;
        setQueue(step.state);
        for (const effect of step.effects) run(effect);
    };

    const answered = async (
        materialId: string,
        sent: PortResult<{
            readonly contentHash: string;
            readonly path?: readonly string[];
        }>,
        refused?: StructuralRun["refused"],
    ) => {
        const r = await sent;
        if (Result.isErr(r) && refused !== undefined) {
            refused(r.error);
            dispatch({ type: "save-failed", materialId });
            return;
        }
        if (Result.isErr(r)) {
            const current = changedElsewhere(r.error);
            if (current !== undefined) {
                dispatch({ type: "save-conflicted", materialId, current });
                return;
            }
            dispatch({ type: "save-failed", materialId });
            deps.current.onRefused();
            return;
        }
        dispatch({
            type: "saved",
            materialId,
            hash: r.value.contentHash,
            path: r.value.path,
        });
        deps.current.onSaved();
    };

    const run = (effect: SaveEffect<StructuralRun>): Promise<void> | void => {
        const { ports: p } = deps.current;
        switch (effect.type) {
            case "send-text":
                return answered(
                    effect.materialId,
                    p.updateElementText(
                        effect.materialId,
                        effect.path,
                        effect.text,
                        effect.hash,
                        effect.base,
                    ),
                );
            case "send-feedback":
                return answered(
                    effect.materialId,
                    p.patchElementAttrs(
                        effect.materialId,
                        effect.path,
                        { feedback: effect.feedback },
                        effect.hash,
                    ),
                );
            case "send-structural":
                return answered(
                    effect.materialId,
                    effect.run.persist(
                        effect.materialId,
                        effect.hash,
                        effect.paths,
                    ),
                    effect.run.refused,
                );
            case "offer-choice":
                deps.current.onConflict(effect.materialId, effect.id);
                return;
            case "show-text":
                deps.current.onShowText(
                    effect.materialId,
                    effect.id,
                    effect.text,
                );
                return;
            case "arm-idle":
                clearTimeout(timer.current);
                timer.current = setTimeout(
                    () =>
                        dispatch({
                            type: "idle-reached",
                            now: Date.now(),
                            held: deps.current.held(),
                        }),
                    effect.ms,
                );
                return;
            default:
                return exhaustSwitchCase(effect);
        }
    };

    // A hidden or closing page sends every draft now (a reload or a closed
    // tab never runs React's cleanups); the command transport keeps a small
    // request alive past the page.
    // biome-ignore lint/correctness/useExhaustiveDependencies: subscribes once; `dispatch` reads the queue from `latest`.
    useEffect(() => {
        const leave = () => dispatch({ type: "page-left" });
        const onHidden = () => {
            if (document.visibilityState === "hidden") leave();
        };
        window.addEventListener("pagehide", leave);
        document.addEventListener("visibilitychange", onHidden);
        return () => {
            window.removeEventListener("pagehide", leave);
            document.removeEventListener("visibilitychange", onHidden);
            clearTimeout(timer.current);
            leave();
        };
    }, []);

    return {
        lane: laneOf(queue, activeMaterialId ?? ""),
        laneNow: (materialId) => laneOf(latest.current, materialId),
        dispatch,
    };
};
